/**
 * V54: 템플릿 선택 모달
 *
 * 편집 버튼 클릭 시 표시되는 템플릿 목록 모달입니다.
 * - 템플릿 목록 표시 (이름, 오브젝트 수, 업데이트일, 운영중 배지)
 * - [새 템플릿 만들기] 버튼
 * - 템플릿 선택 후 [선택한 템플릿 편집하기] 버튼
 *
 * @module pages/organization/components/seatMap/TemplateListModal
 */

import React, { useState } from 'react';
import { X, Plus, FileEdit, Check, Loader, Trash2 } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import { SeatMapTemplateListItem } from '../../../../shared/types/seatMap';

// ============================================
// Props 타입
// ============================================

export interface TemplateListModalProps {
  /** 모달 열림 상태 */
  isOpen: boolean;
  /** 모달 닫기 핸들러 */
  onClose: () => void;
  /** 템플릿 목록 */
  templates: SeatMapTemplateListItem[];
  /** 템플릿 목록 로딩 중 */
  isLoading: boolean;
  /** 뮤테이션 진행 중 */
  isProcessing: boolean;
  /** 새 템플릿 만들기 핸들러 */
  onCreateNew: () => void;
  /** 템플릿 선택 후 편집 시작 핸들러 */
  onSelectTemplate: (templateId: string) => void;
  /** 템플릿 삭제 핸들러 */
  onDeleteTemplate: (templateId: string) => void;
}

// ============================================
// 날짜 포맷 헬퍼
// ============================================

function formatDate(date: Date | { seconds: number; nanoseconds: number } | undefined): string {
  if (!date) return '-';

  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else if (typeof date === 'object' && 'seconds' in date) {
    // Firestore Timestamp
    d = new Date(date.seconds * 1000);
  } else {
    return '-';
  }

  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // 오늘
    return `오늘 ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } else if (diffDays === 1) {
    return '어제';
  } else if (diffDays < 7) {
    return `${diffDays}일 전`;
  } else {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  }
}

// ============================================
// 모달 컴포넌트
// ============================================

export function TemplateListModal({
  isOpen,
  onClose,
  templates,
  isLoading,
  isProcessing,
  onCreateNew,
  onSelectTemplate,
  onDeleteTemplate,
}: TemplateListModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSelect = (templateId: string) => {
    setSelectedId(templateId);
  };

  const handleStartEdit = () => {
    if (selectedId) {
      onSelectTemplate(selectedId);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, templateId: string) => {
    e.stopPropagation();
    setDeleteConfirmId(templateId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmId) {
      onDeleteTemplate(deleteConfirmId);
      setDeleteConfirmId(null);
      if (selectedId === deleteConfirmId) {
        setSelectedId(null);
      }
    }
  };

  const selectedTemplate = templates.find((t) => t.templateId === selectedId);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '560px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--color-gray-800)',
              margin: 0,
            }}
          >
            편집할 템플릿 선택
          </h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isProcessing ? 0.5 : 1,
            }}
          >
            <X size={20} color="var(--color-gray-500)" />
          </button>
        </div>

        {/* 새 템플릿 만들기 버튼 */}
        <button
          onClick={onCreateNew}
          disabled={isProcessing}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            border: '2px dashed var(--color-gray-300)',
            borderRadius: '8px',
            background: 'var(--color-gray-50)',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            marginBottom: '16px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!isProcessing) {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.background = 'var(--color-blue-50)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-gray-300)';
            e.currentTarget.style.background = 'var(--color-gray-50)';
          }}
        >
          <Plus size={20} color="var(--color-primary)" />
          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-primary)' }}>
            새 템플릿 만들기
          </span>
        </button>

        {/* 템플릿 목록 */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: '16px',
            border: '1px solid var(--color-gray-200)',
            borderRadius: '8px',
          }}
        >
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                color: 'var(--color-gray-500)',
              }}
            >
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '8px' }}>템플릿 목록 로딩 중...</span>
            </div>
          ) : templates.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                color: 'var(--color-gray-500)',
              }}
            >
              <FileEdit size={48} color="var(--color-gray-300)" />
              <p style={{ marginTop: '12px', fontSize: '14px' }}>
                템플릿이 없습니다. 새 템플릿을 만들어주세요.
              </p>
            </div>
          ) : (
            <div style={{ padding: '8px' }}>
              {templates.map((template) => {
                const isSelected = selectedId === template.templateId;
                const isDeleting = deleteConfirmId === template.templateId;

                return (
                  <div
                    key={template.templateId}
                    onClick={() => handleSelect(template.templateId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      marginBottom: '4px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: isSelected ? 'var(--color-blue-50)' : 'transparent',
                      border: isSelected ? '2px solid var(--color-primary)' : '2px solid transparent',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'var(--color-gray-50)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {/* 선택 체크 */}
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: isSelected
                          ? '2px solid var(--color-primary)'
                          : '2px solid var(--color-gray-300)',
                        background: isSelected ? 'var(--color-primary)' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && <Check size={12} color="white" />}
                    </div>

                    {/* 템플릿 정보 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'var(--color-gray-800)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {template.name}
                        </span>
                        {template.isActive && (
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: 'white',
                              background: 'var(--color-green-600)',
                              padding: '2px 8px',
                              borderRadius: '10px',
                            }}
                          >
                            운영중
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--color-gray-500)',
                          marginTop: '2px',
                        }}
                      >
                        {template.objectCount}개 오브젝트 · {formatDate(template.updatedAt as any)}
                      </div>
                    </div>

                    {/* 삭제 버튼 (운영중이 아닌 경우만) */}
                    {!template.isActive && (
                      <button
                        onClick={(e) => handleDeleteClick(e, template.templateId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '4px',
                          cursor: 'pointer',
                          opacity: 0.5,
                          transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.5';
                        }}
                        title="삭제"
                      >
                        <Trash2 size={16} color="var(--color-red-500)" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 삭제 확인 다이얼로그 */}
        {deleteConfirmId && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '16px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                width: '320px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              }}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>
                템플릿 삭제
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: 'var(--color-gray-600)' }}>
                이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  취소
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isProcessing}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    background: 'var(--color-red-500)',
                    color: 'white',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {isProcessing ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 하단 버튼 */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={isProcessing}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              background: 'white',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--color-gray-600)',
            }}
          >
            취소
          </button>
          <button
            onClick={handleStartEdit}
            disabled={!selectedId || isProcessing}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              background: selectedId ? 'var(--color-primary)' : 'var(--color-gray-300)',
              color: 'white',
              cursor: !selectedId || isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {isProcessing ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                처리 중...
              </>
            ) : (
              <>
                <FileEdit size={16} />
                선택한 템플릿 편집하기
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
